import Link from "next/link";
import { GetStaticProps, InferGetStaticPropsType } from "next";

import { Button, Card, Disclosure } from "@chef/components";
import { ChevronRight, Email, Phone } from "@chef/icons/small";
import { CONTACT, BRAND_PATHS } from "@chef/constants";
import {
  BlockContent,
  getOpeningHoursData,
  IOpeningHours,
  SanityQuery,
  useOpeningHoursData,
} from "@chef/feature-sanity";
import { AppShell } from "@chef/feature-app-shell";
import { AccountStatusWarning } from "@chef/smart-components";
import { useTrack } from "@chef/state-management/hooks";

import { MyAccountLayout } from "../components/MyAccountLayout";

import { intl } from "./MyAccountContactUsPage.Intl";

export const MyAccountContactUsPage = (
  props: InferGetStaticPropsType<typeof getOpeningHoursStaticProps>,
) => {
  const [chat] = useOpeningHoursData(props.chatOpeningHours);
  const [phone] = useOpeningHoursData(props.phoneOpeningHours);

  const track = useTrack();

  if (!phone || !chat) {
    return null;
  }

  return (
    <AppShell title={intl.CONTACT_US}>
      <AppShell.Meta />
      <AppShell.Header />

      <AppShell.Main grid>
        <MyAccountLayout page="contactUs">
          <div className="flex flex-col gap-6 xl:col-span-5 md:col-start-4 md:col-span-5 col-span-full">
            <AccountStatusWarning />
            <Card id="chat-with-us-card">
              <h2 className="mb-4 text-xl">
                <strong>{intl.CHAT_WITH_US}</strong>
              </h2>
              <BlockContent body={chat.body} prose={{ disabled: true }} />
            </Card>

            <Card id="faq-card">
              <h2 className="text-base">
                <strong>{intl.FAQ_TITLE}</strong>
              </h2>
              <p className="mt-1 text-sm">{intl.FAQ_SUBTEXT}</p>
              <Button
                outlined
                className="flex my-6"
                IconRight={() => <ChevronRight className="ml-2" />}
                id="faq-btn"
                href={BRAND_PATHS.FAQ_HREF}
              >
                <strong className="text-sm">{intl.SEE_FAQ}</strong>
              </Button>
            </Card>

            <Card id="email-card">
              <Disclosure
                Title={() => (
                  <span className="text-sm">
                    <strong>{intl.SEND_MAIL}</strong>
                  </span>
                )}
                Icon={() => <Email className="mr-2" />}
                defaultOpen={false}
              >
                <p className="text-sm">
                  {intl.SEND_MAIL_TO}{" "}
                  <a
                    href={`mailto:${CONTACT.EMAIL}`}
                    className="underline"
                    onClick={() => {
                      track("csContactStarted", {
                        affiliation: "Frontend process",
                        contact_type: "email",
                      });
                    }}
                  >
                    <strong>{CONTACT.EMAIL}</strong>
                  </a>
                </p>
              </Disclosure>
            </Card>

            <Card id="phone-card">
              <Disclosure
                Title={() => (
                  <span className="text-sm">
                    <strong>{intl.CALL_US}</strong>
                  </span>
                )}
                Icon={() => <Phone className="mr-2" />}
                defaultOpen={false}
              >
                <div className="mt-4">
                  <BlockContent body={phone.body} prose={{ disabled: true }} />
                </div>
              </Disclosure>
            </Card>

            <Card>
              <Disclosure
                Title={() => (
                  <div className="text-left">
                    <span className="text-sm">
                      <strong>{intl.SEND_US_A_CLAIM}</strong>
                    </span>
                    <p>{intl.TELL_US_WHAT_WENT_WRONG}</p>
                  </div>
                )}
                defaultOpen={false}
              >
                <Link href={BRAND_PATHS.CLAIM_PAGE} passHref>
                  <Button
                    outlined
                    IconRight={() => <ChevronRight className="ml-2" />}
                  >
                    <strong className="text-sm">
                      {intl.REPORT_PROBLEMS_OR_MISSING_ITEMS}
                    </strong>
                  </Button>
                </Link>
              </Disclosure>
            </Card>
          </div>
        </MyAccountLayout>
      </AppShell.Main>

      <AppShell.Footer minified />
      <AppShell.TabBar />
    </AppShell>
  );
};

export const getOpeningHoursStaticProps: GetStaticProps<
  {
    draftMode: boolean;
    chatOpeningHours: IOpeningHours;
    phoneOpeningHours: IOpeningHours;
  },
  SanityQuery
> = async ({ draftMode = false }) => {
  const chatOpeningHours = await getOpeningHoursData("chat_opening_hours", {
    preview: draftMode,
  });

  const phoneOpeningHours = await getOpeningHoursData("phone_opening_hours", {
    preview: draftMode,
  });

  if (!chatOpeningHours && !phoneOpeningHours) {
    return {
      notFound: true,
      revalidate: 600,
    };
  }

  return {
    props: {
      draftMode,
      chatOpeningHours,
      phoneOpeningHours,
    },
    revalidate: 600,
  };
};
